import React from "react";
import { ReactCookieProps, useCookies } from "react-cookie";
import { AgeGateCookieEnum } from "../../utils/enums";
import { initGTagCallback, safeGTag, triggerGTagCallback } from "../../utils/gtag-config";

export const enum ConsentFlag {
    AllowAll,
    AnalyticsAndFunctionality,
    Analytics,
    Functionality,
    Necessary,
}

export interface ConsentState {
    showAgeGate: boolean;
    acceptAgeGate: (consentFlag: ConsentFlag) => void;
}

export const ConsentContext = React.createContext<ConsentState>({
    showAgeGate: true,
    acceptAgeGate: (_) => {},
});
export default ConsentContext;

/**
 * Wrapper for the Google Tag interface to grant/deny permission to use storage for Google Analytics tracking.
 *
 * @param status {('granted' | 'denied')} - Passed to `gtag`'s consent setting for all storage options.
 * @returns {void}
 * */
function setTag(status: "granted" | "denied"): void {
    // Check that `gtag` exists on the window.  If so, update the consent status with
    // either `granted` or `denied` as appropriate.
    safeGTag("consent", "update", {
        ad_storage: status,
        analytics_storage: status,
    });

    // Allow gatsby-browser onRouteUpdate to take over firing page_view events.
    // Guard to prevent "double" firing the initial page view event.
    if (!initGTagCallback()) {
        triggerGTagCallback(window);
    }
}

export const ConsentContextWrapper: React.FC<React.PropsWithChildren> = (props) => {
    const [cookies, setCookies] = useCookies([AgeGateCookieEnum.CookieName]);
    const [showAgeGate, setShowAgeGate] = React.useState(![AgeGateCookieEnum.ConsentAllValue, AgeGateCookieEnum.ConsentReqValue].includes(cookies.ageCheck));

    /**
     * Callback for the `AgeGateComponent` which handles setting the appropriate cookie values.  Gets passed through
     * the ConsentContext.Consumer into the <AgeGateComponent />.
     * */
    function acceptAgeGate(consentFlag: ConsentFlag): void {
        let allowFunctionality: boolean;
        let allowAnalytics: boolean;

        switch (consentFlag) {
            case ConsentFlag.AllowAll:
            case ConsentFlag.AnalyticsAndFunctionality:
                allowFunctionality = allowAnalytics = true;
                break;
            case ConsentFlag.Analytics:
                allowAnalytics = true;
                allowFunctionality = false;
                break;
            case ConsentFlag.Functionality:
                allowAnalytics = false;
                allowFunctionality = true;
                break;
            case ConsentFlag.Necessary:
                allowFunctionality = allowAnalytics = false;
                break;
        }

        // Init without `expires` or `maxAge`, so the default cookies are session-based.
        // If the user has selected `rememberMe`, then set a `maxAge`.
        const baseOptions: ReactCookieProps["defaultSetOptions"] = {
            domain: window.location.hostname
                .split(".")
                .filter((s) => s !== "www")
                .join("."),
            path: "/",
            sameSite: "lax",
            maxAge: AgeGateCookieEnum.CookieDuration as NonNullable<ReactCookieProps["defaultSetOptions"]>["maxAge"],
        };

        if (allowFunctionality) {
            setCookies(AgeGateCookieEnum.CookieName, allowAnalytics ? AgeGateCookieEnum.ConsentAllValue : AgeGateCookieEnum.ConsentReqValue, baseOptions);
        }

        setTag(allowAnalytics ? "granted" : "denied");

        setShowAgeGate(false);
    }

    const providerState: ConsentState = {
        showAgeGate,
        acceptAgeGate: acceptAgeGate.bind(this),
    };

    // Run this in `useEffect` to ensure it's on the client site (and thus `window` exists).
    React.useEffect(() => {
        const initValue = [AgeGateCookieEnum.ConsentAllValue, AgeGateCookieEnum.ConsentReqValue].includes(cookies.ageCheck)
            ? cookies.ageCheck === AgeGateCookieEnum.ConsentAllValue
                ? "granted"
                : "denied"
            : null; // Prevents setTag from being fired if the AgeGateCookie isn't set yet.
        if (!!initValue) {
            setTag(initValue);
        }
    }, []);

    return <ConsentContext.Provider value={providerState}>{props.children}</ConsentContext.Provider>;
};
